import React, { useEffect } from "react";
import bg from "../assets/about3.png";
import { Link } from "react-router-dom";
import GenericApi from "@/api/genericApi";
import { ApiRoutes } from "@/config/routes/ApiRoutes";
import { ApiError, ApiResponse } from "@/types/Api";
import {
  ForgotPasswordRequest,
  ForgotPasswordRequestSchema,
} from "@/types/Auth";
import { FormError, ValidationErrors } from "@/types/ValidationError";
import CustomLogger from "@/utils/CustomLogger";
import { useMutation } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { formatError } from "@/utils/formatError";
import { AxiosError, isAxiosError } from "axios";
import FormField from "@/components/CustomForm/FormField";
import { FormFieldType } from "@/types/FormFieldsType.tsx";
import { useSnackBarAlert } from "@/hooks/useSnackbar.tsx";
import { isBitramp } from "@/utils/utils";
import bg_Bitramp from "../assets/SVG/bg_Bitramp.svg";

const ForgotPassword: React.FC = () => {
  const { showSnackBar } = useSnackBarAlert();

  useEffect(() => {
    const themeClass = isBitramp() ? "theme-bitramp" : "theme-leonext";
    document.body.classList.add(themeClass);

    return () => {
      document.body.classList.remove("theme-bitramp");
    };
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<ForgotPasswordRequest>({
    resolver: zodResolver(ForgotPasswordRequestSchema),
  });

  const forgotPasswordMutation = useMutation<
    ApiResponse<null>,
    ApiError<ValidationErrors>,
    ForgotPasswordRequest
  >({
    mutationFn: (data) =>
      GenericApi.put<null>(ApiRoutes.SEND_FORGOT_PASSWORD_EMAIL, {
        ...data,
      }),
    onSuccess: async () => {
      CustomLogger.log("Password reset link sent successfully.");
      showSnackBar(
        "A password reset link has been sent to your email.",
        "success",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000
      );
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      handleAxiosError(error);
      showSnackBar(
        "An error occurred",
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000
      );
    },
  });

  const handleAxiosError = (error: AxiosError<unknown> | null | Error) => {
    if (isAxiosError<FormError>(error)) {
      setError("root", {
        type: "manual",
        message:
          error.response?.data?.detail ??
          "An error occurred while requesting a password reset",
      });
      return;
    }

    if (isAxiosError<ValidationErrors>(error)) {
      setError("root", {
        type: "manual",
        message: formatError(
          error,
          "An error occurred while requesting a password reset"
        ),
      });
      return;
    }

    setError("root", {
      type: "manual",
      message:
        error?.message ?? "An error occurred while requesting a password reset",
    });
  };

  const onSubmit = async (data: ForgotPasswordRequest) => {
    forgotPasswordMutation.mutate(data);
  };

  return isBitramp() ? (
    <div className="login">
      <img
        draggable="false"
        className="login__bg__img_bitramp"
        src={bg_Bitramp}
        alt="bg"
      />
      <div className="login__container !w-[50%] !rounded-[1.5625rem] !bg-[#262626] !border !border-[#262626] !shadow-none">
        <div className="login__container__top !text-white !text-[3rem]">
          <Link to="/login" className="login__container__top__arrow">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="24"
              viewBox="0 0 15 24"
              fill="none"
            >
              <path
                d="M13 2L2.81394 11.2601C2.3775 11.6568 2.3775 12.3432 2.81393 12.7399L13 22"
                stroke="#fff"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
          </Link>
          <div className="ml-3 text-5xl w-full">Forgot Password</div>
        </div>
        <form
          className="login__container__fields"
          onSubmit={handleSubmit(onSubmit)}
        >
          <FormField<ForgotPasswordRequest>
            type={FormFieldType.EMAIL}
            placeholder="Email Address"
            name="email"
            register={register}
            error={errors.email}
            label="Email Address"
            valueAsNumber={false}
            required={true}
            labelStyle="login__container__fields__title !text-[1.2rem] !text-white"
            inputStyle="login__container__fields__space !bg-[#2B2B2B] !border !border-[#464646] !text-white placeholder:text-[1rem] focus:outline-none focus:ring-0 focus:border-none"
          />
          <button
            type="submit"
            className="Primary_button w-[12.3125rem] h-[2.6875rem]"
          >
            {forgotPasswordMutation.isPending
              ? "Loading..."
              : "Request Reset Link"}
          </button>
        </form>
        {forgotPasswordMutation.isError ? (
          <span className="block text-red-500 pt-4 sm:inline">
            {errors.root?.message}
          </span>
        ) : null}
        <div className="login__container__switch">
          <div className="login__container__switch__text !text-white font-[400] text-[1rem]">
            Remembered your password?
          </div>
          <Link
            to="/login"
            className="login__container__switch__link !text-[#8AFEFD] text-[1rem]"
          >
            Log In
          </Link>
        </div>
      </div>
    </div>
  ) : (
    <div className="login">
      <img draggable="false" className="login__bg__img" src={bg} alt="bg" />
      <div className="login__container !w-[50%]">
        <div className="login__container__top">
          <Link to="/login" className="login__container__top__arrow">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="24"
              viewBox="0 0 15 24"
              fill="none"
            >
              <path
                d="M13 2L2.81394 11.2601C2.3775 11.6568 2.3775 12.3432 2.81393 12.7399L13 22"
                stroke="#05004E"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
          </Link>
          <div className="ml-3 text-[3rem] w-full">Forgot Password</div>
        </div>
        <form
          className="login__container__fields"
          onSubmit={handleSubmit(onSubmit)}
        >
          <FormField<ForgotPasswordRequest>
            type={FormFieldType.EMAIL}
            placeholder="Email Address"
            name="email"
            register={register}
            error={errors.email}
            label="Email Address"
            valueAsNumber={false}
            required={true}
            labelStyle="login__container__fields__title !text-[1.2rem]"
            inputStyle="login__container__fields__space Input placeholder:text-[1rem]"
          />
          <button
            type="submit"
            className="Primary_button w-[12.3125rem] h-[2.6875rem]"
          >
            {forgotPasswordMutation.isPending
              ? "Loading..."
              : "Request Reset Link"}
          </button>
        </form>
        {forgotPasswordMutation.isError ? (
          <span className="block text-red-500 pt-4 sm:inline">
            {errors.root?.message}
          </span>
        ) : null}
        <div className="login__container__switch">
          <div className="login__container__switch__text">
            Remembered your password?
          </div>
          <Link to="/login" className="login__container__switch__link">
            Log In
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
